import { ComponentFactory, FunctionalComponent, h } from 'preact';

import { configGetLanguage } from 'common/helper/config/get-language';
import { domClassMerge } from 'common/helper/dom/class-merge';
import { galleryGetSlidingStyles } from 'common/helper/gallery/get-sliding-styles';
import { stringToggle } from 'common/helper/string/toggle';
import { GalleryNavigationTemplate } from 'common/module/gallery/navigation/template';
import { Gallery2Template } from 'common/module/gallery-2/template';
import { prepareClassificationLabel } from 'common/module/gallery-scroll/prepare-classification-label.util';
import { GalleryScrollIndicatorComponent } from 'common/module/gallery-scroll-indicator/component';
import { GalleryScrollIndicatorComponentPropsInterface } from 'common/module/gallery-scroll-indicator/component-props.interface';
import { ImageGalleryObjectFitEnum } from 'common/module/image-gallery/object-fit.enum';
import { ImageGalleryTemplatePropsInterface } from 'common/module/image-gallery/template-props.interface';

import { GalleryNudgeWhatsappComponent } from '../gallery/nudge/whatsapp/component';
import { ImageGalleryItemTypeEnum } from '../image-gallery/item-type.enum';
import { GalleryScrollItemTemplate } from './item-template';

/**
 * Template for gallery scroll
 */
export const GalleryScrollTemplate: FunctionalComponent<
  ImageGalleryTemplatePropsInterface & {
    indicatorComponent?: ComponentFactory<GalleryScrollIndicatorComponentPropsInterface>;
    whatsappShown?: boolean;
  }
> = (props) => {
  const IndicatorComponent = props.indicatorComponent || GalleryScrollIndicatorComponent;
  const { objectFit = ImageGalleryObjectFitEnum.NONE, whatsappShown = true } = props;
  const itemStyles = `object-fit:${objectFit};`;
  const locale = configGetLanguage().current;

  return (
    <div className={`gallery-scroll ${stringToggle(props.className)(!!props.className)}`}>
      <Gallery2Template
        onMouseDown={(e) => {
          if (props.onScroll) {
            props.onScroll();
          }
          props.gallery.onMouseDown(e);
        }}
        onTouchStart={(e) => {
          if (props.onScroll) {
            props.onScroll();
          }
          props.gallery.onTouchStart(e);
        }}
        onClick={props.gallery.onClick}
        onGalleryRef={props.gallery.onGalleryRef}
        className='gallery-scroll__gallery'
      >
        {props.items.map((item, i) => {
          const galleryItemProps = {
            item,
            index: i,
            currentIndex: props.gallery.currentIndex,
            webp: props.feature.webp,
            alt: prepareClassificationLabel(locale, item.classificationLabel, props.alt, i),
            title: prepareClassificationLabel(locale, item.classificationLabel, props.alt, i),
            placeholder: props.placeholder,
          };

          if (whatsappShown && item.type === ImageGalleryItemTypeEnum.NUDGE) {
            return (
              <GalleryNudgeWhatsappComponent
                key={`${item.type}-${item.sourceUrl}`}
                propertyId={item.propertyId}
                style={`${itemStyles}${props.items.length > 1 ? galleryGetSlidingStyles(props.gallery, i) : ''}`}
              >
                <GalleryScrollItemTemplate {...galleryItemProps} />
              </GalleryNudgeWhatsappComponent>
            );
          }

          return (
            (props.gallery.visitedIndexes[i] || props.isShowingAll) && (
              <GalleryScrollItemTemplate
                key={`${item.type}-${item.sourceUrl}`}
                {...galleryItemProps}
                style={`${itemStyles}${props.items.length > 1 ? galleryGetSlidingStyles(props.gallery, i) : ''}`}
              />
            )
          );
        })}
      </Gallery2Template>

      <IndicatorComponent
        mainClass={'gallery-scroll__gallery-scroll-indicator'}
        itemsCount={props.items.length}
        itemWidth={10}
        activeIndex={props.gallery.currentIndex}
        visibleItemsCount={3}
      />

      <div
        className={domClassMerge('gallery-scroll__gallery-navigation', {
          'gallery-scroll__gallery-navigation-display': props.isNavigationVisible,
        })}
      >
        <GalleryNavigationTemplate />
      </div>
    </div>
  );
};
